<script lang="ts">
	import { afterNavigate, replaceState } from '$app/navigation';
	import { page } from '$app/state';
	import { createDialog, melt } from '@melt-ui/svelte';
	import type { ChangeFn } from '@melt-ui/svelte/internal/helpers';
	import { SquareCheck, Star } from 'lucide-svelte';
	import { onMount } from 'svelte';
	import SignupModal from './SignupModal.svelte';
	import { addToast } from './Toaster.svelte';
	import { userStore } from './stores/user';
	import { watchlistStore } from './stores/watchlist';
	import { authenticatedFetch, getWatchlist } from './utils/auth';

	interface Props {
		symbol: Populated<BaseSymbol, never>;
		align?: 'left' | 'right';
		silentFollow?: boolean;
	}

	let { symbol, align = 'left', silentFollow = false }: Props = $props();

	const {
		elements: { content },
		states: { open }
	} = createDialog({
		role: 'alertdialog'
	});

	const handleOpenChange: ChangeFn<boolean> = ({ next }) => {
		if (next) replaceState(page.url.toString(), { from: `${page.url.pathname}?following=true` });
		else replaceState(page.url.pathname, {});
		return next;
	};

	async function handleAdd() {
		disabled = true;
		try {
			await authenticatedFetch('/private/watchlist/add', {
				method: 'POST',
				body: JSON.stringify({ symbolId: symbol.documentId })
			});
			$watchlistStore.symbols = [...$watchlistStore.symbols, symbol.documentId];
			if (!silentFollow) $open = true;
		} catch (error) {
			addToast({
				data: {
					status: 'error',
					message: 'An error occurred while adding to watchlist'
				}
			});
		}
		disabled = false;
	}

	async function handleRemove() {
		disabled = true;
		try {
			await authenticatedFetch('/private/watchlist/remove', {
				method: 'POST',
				body: JSON.stringify({ symbolId: symbol.documentId })
			});
			$watchlistStore.symbols = $watchlistStore.symbols.filter(
				(documentId) => documentId !== symbol.documentId
			);
		} catch (error) {
			addToast({
				data: {
					status: 'error',
					message: 'An error occurred while removing from watchlist'
				}
			});
		}
		disabled = false;
	}

	let disabled = $state(false);

	onMount(async () => {
		try {
			const watchlist = await getWatchlist();
			$watchlistStore.symbols = watchlist.symbols.map(({ documentId }) => documentId);
		} catch (error) {
			/* no-op */
		}
	});

	afterNavigate(async ({ type }) => {
		if (type !== 'goto') return;
		const following = page.url.searchParams.get('following');
		if (following === 'true' && !$watchlistStore.symbols.includes(symbol.documentId)) {
			await handleAdd();
			replaceState(page.url.pathname, {});
		}
	});
</script>

<div class="relative">
	{#if $userStore.signedIn}
		{#if $watchlistStore.symbols.includes(symbol.documentId)}
			<button {disabled} onclick={handleRemove} class="btn-small btn-secondary-icon group">
				<Star fill="#ffb630" class="text-yellow-mid mr-2 h-3 w-3" />
				<span>Following</span>
			</button>
		{:else}
			<button
				{disabled}
				onclick={handleAdd}
				class="btn-small btn-secondary-icon group"
				data-type="add-to-watchlist"
				data-content="signed-in"
			>
				<Star class="text-green-dark group-hover:text-yellow-mid mr-2 h-3 w-3" />
				<span>Follow</span>
			</button>
		{/if}
	{:else}
		<SignupModal onOpenChange={handleOpenChange}>
			{#snippet children(trigger)}
				<button
					use:melt={trigger}
					class="btn-small btn-secondary-icon group"
					data-type="add-to-watchlist"
					data-content="signed-out"
				>
					<Star class="text-green-dark group-hover:text-yellow-mid mr-2 h-3 w-3" />
					<span>Follow</span>
				</button>
			{/snippet}
		</SignupModal>
	{/if}
	{#if $open}
		<div
			class="bg-white-pure absolute top-full z-50 w-80 translate-y-2 rounded-xl p-4 shadow-lg {align}-0"
			use:melt={$content}
		>
			<div class="index-bold mx-auto max-w-sm">Added to Watchlist</div>
			<hr class="border-gray-faint my-5" />
			<div class="flex items-center">
				<SquareCheck class="fill-green-primary text-white-pure mr-3 w-6" />
				<div class="flex grow justify-between">
					<p class="index-regular">My Watchlist</p>
					<a href="/watchlist" class="link link-primary index-bold">Open</a>
				</div>
			</div>
		</div>
	{/if}
</div>
